import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '../ui/kit.js';

/**
 * Reads the QR code the tablet is showing.
 *
 * Two paths, because one is not available everywhere:
 *
 * - `BarcodeDetector`, where the browser has it (Chromium). Native, fast, and
 *   nothing to download.
 * - a decoder in JavaScript otherwise (Safari, Firefox), imported only when it
 *   is actually needed so that the majority of parents never pay for it.
 *
 * The camera stream is acquired once here and handed to whichever path runs.
 * Frames are decoded and discarded: nothing is captured, stored or uploaded.
 */

interface QrScannerProps {
  /**
   * Called with whatever the camera decoded, which may well be a QR code from
   * a cereal box. Deciding what it means is the caller's job.
   */
  onResult: (value: string) => void;
}

type ScannerState = 'starting' | 'scanning' | 'denied' | 'unavailable';

export const QrScanner = ({ onResult }: QrScannerProps) => {
  const { t } = useTranslation();
  const videoRef = useRef<HTMLVideoElement>(null);
  const [state, setState] = useState<ScannerState>('starting');

  const latestResult = useRef(onResult);
  latestResult.current = onResult;

  useEffect(() => {
    let stream: MediaStream | null = null;
    let frame: number | null = null;
    let stopDecoder: (() => void) | null = null;
    let cancelled = false;

    const emit = (value: string) => {
      latestResult.current(value);
    };

    const scanWithBarcodeDetector = (video: HTMLVideoElement, detector: BarcodeDetectorLike) => {
      const tick = async () => {
        if (cancelled) {
          return;
        }

        try {
          const [barcode] = await detector.detect(video);
          if (barcode !== undefined) {
            emit(barcode.rawValue);
          }
        } catch {
          // A frame that will not decode is the normal case while the parent
          // is still aiming. The next one is a few milliseconds away.
        }

        if (!cancelled) {
          frame = requestAnimationFrame(() => void tick());
        }
      };

      frame = requestAnimationFrame(() => void tick());
    };

    const scanWithFallbackDecoder = async (video: HTMLVideoElement, source: MediaStream) => {
      const { BrowserQRCodeReader } = await import('@zxing/browser');
      if (cancelled) {
        return;
      }

      const controls = await new BrowserQRCodeReader().decodeFromStream(source, video, (result) => {
        if (result !== undefined) {
          emit(result.getText());
        }
      });
      stopDecoder = () => controls.stop();
    };

    const start = async () => {
      const video = videoRef.current;
      if (video === null) {
        return;
      }

      try {
        stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: 'environment' },
        });
      } catch {
        // Refused, or no camera at all. Either way the parent types the code.
        setState('denied');
        return;
      }

      if (cancelled) {
        return;
      }

      video.srcObject = stream;
      await video.play().catch(() => undefined);
      setState('scanning');

      const detector = window.BarcodeDetector;
      if (detector !== undefined) {
        scanWithBarcodeDetector(video, new detector({ formats: ['qr_code'] }));
        return;
      }

      try {
        await scanWithFallbackDecoder(video, stream);
      } catch {
        setState('unavailable');
      }
    };

    void start();

    return () => {
      cancelled = true;
      if (frame !== null) {
        cancelAnimationFrame(frame);
      }
      stopDecoder?.();
      for (const track of stream?.getTracks() ?? []) {
        track.stop();
      }
    };
  }, []);

  return (
    <div className="scanner">
      <div className="viewfinder">
        {/* Muted and inline so that iOS plays it without a full-screen takeover. */}
        <video ref={videoRef} muted playsInline aria-label={t('pairing.cameraLabel')}>
          <track kind="captions" />
        </video>
        {state === 'scanning' ? (
          <>
            <div className="frame" />
            <div className="scanline" />
          </>
        ) : (
          <span className="scanner-idle">
            <Icon name="camera" />
          </span>
        )}
      </div>
      <p className="help scanner-hint" aria-live="polite">
        {t(`pairing.scanner.${state}`)}
      </p>
    </div>
  );
};
